import React, { useState } from 'react';
import { X, Check, Crown, Mail } from 'lucide-react';
import { subscriptionsApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import '../pages/Pages.css';

interface UpgradeModalProps {
  currentPlan: string;
  preselectedPlan?: string;
  onClose: () => void;
  onUpgraded?: () => void;
}

const PLANS = [
  {
    key: 'basic',
    name: 'Einzelcoach',
    price: '49',
    subtitle: 'Für Einzelcoaches',
    maxUsers: '1 Nutzer',
    popular: false,
    features: [
      'Unbegrenzte Klienten & Sitzungen',
      'Kalender & Buchungsseite',
      'ZUGFeRD-Rechnungen & Angebote',
      'Coaching-Pakete & Kontingente',
      'Erstgespräch mit Vertrag & Signatur',
      'Follow-Up-Modul',
      'Sitzungsprotokolle & PDF-Export',
      'Google Drive (5 Kategorien)',
      'Zoom-Integration',
      'DSGVO-Modul & Audit-Log',
    ],
  },
  {
    key: 'team_s',
    name: 'Professional S',
    price: '99',
    subtitle: 'Bis 5 Coaches',
    maxUsers: '5 Nutzer',
    popular: true,
    features: [
      'Alles aus Einzelcoach',
      'Bis 5 Nutzer / Coaches',
      'Rollen & Rechte-Verwaltung',
      'Klienten-Zuweisung im Team',
      'Team-Kalender',
      'Kunden-Portal',
      'Intake-Formulare',
      'E-Mail-Vorlagen & Platzhalter',
      'Vertragsvorlagen-Editor',
      'Mahnwesen (Stufe 1–3)',
    ],
  },
  {
    key: 'team_m',
    name: 'Professional M',
    price: '179',
    subtitle: 'Bis 15 Coaches',
    maxUsers: '15 Nutzer',
    features: [
      'Alles aus Professional S',
      'Bis 15 Nutzer / Coaches',
      'ISO 9001 QM-Modul',
      'Dashboard & Reports (Charts)',
      'Webhooks & API-Zugang',
      'Dark Mode / Theme Switcher',
      'Eigenes Branding (White Label)',
      'Cookie-Banner & AVV',
      'Prioritäts-Support',
    ],
  },
  {
    key: 'enterprise',
    name: 'Organisation',
    price: null,
    subtitle: 'Unbegrenzte Nutzer',
    maxUsers: 'Unbegrenzt',
    features: [
      'Alles aus Professional M',
      'Unbegrenzte Nutzer',
      'SSO / SAML',
      'Dedizierter Account Manager',
      'Custom Integrations',
      'SLA-Garantie (99.9%)',
      'On-Premise Option',
      'Migration bestehender Daten',
    ],
  },
];

const PLAN_ORDER = ['free', 'basic', 'team_s', 'team_m', 'enterprise'];

export default function UpgradeModal({ currentPlan, preselectedPlan, onClose, onUpgraded }: UpgradeModalProps) {
  const { showToast } = useToast();
  const { subscription } = useAuth();
  const [selected, setSelected] = useState(preselectedPlan || 'basic');
  const [changing, setChanging] = useState(false);

  const currentIdx = PLAN_ORDER.indexOf(currentPlan);
  const selectedIdx = PLAN_ORDER.indexOf(selected);
  const isUpgrade = selectedIdx > currentIdx;
  const isTrial = !!(subscription && (subscription.status === 'trial' || subscription.status === 'expired'));

  const getButtonText = () => {
    if (changing) return 'Wird gewechselt...';
    if (selected === 'enterprise') return 'Kontakt aufnehmen';
    if (isTrial && isUpgrade) return 'Kontakt für Upgrade';
    const planName = PLANS.find(p => p.key === selected)?.name;
    return isUpgrade ? `Auf ${planName} upgraden` : `Auf ${planName} wechseln`;
  };

  const handlePlanChange = async () => {
    if (selected === 'enterprise' || (isTrial && isUpgrade)) {
      const planName = PLANS.find(p => p.key === selected)?.name || selected;
      window.open(`mailto:service@coachdesk365.com?subject=CoachDesk365 Upgrade auf ${encodeURIComponent(planName)}&body=${encodeURIComponent(`Hallo,\n\nich möchte gerne auf den Plan "${planName}" upgraden.\n\nMit freundlichen Grüßen`)}`, '_blank');
      return;
    }
    setChanging(true);
    try {
      await subscriptionsApi.upgrade(selected);
      showToast(`Erfolgreich auf ${PLANS.find(p => p.key === selected)?.name} gewechselt!`, 'success');
      onUpgraded?.();
      onClose();
    } catch (err: any) {
      showToast(err.response?.data?.detail || 'Fehler beim Planwechsel', 'error');
    } finally {
      setChanging(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal upgrade-modal" style={{ padding: '32px 28px' }}>
        <div className="flex-between mb-24">
          <div>
            <h2 style={{ margin: 0, fontSize: '1.3rem' }}>Plan wechseln</h2>
            <p className="text-muted text-sm" style={{ margin: '4px 0 0' }}>
              Wechseln Sie jederzeit — keine Bindung.
            </p>
          </div>
          <button className="btn-icon" onClick={onClose}><X size={20} /></button>
        </div>

        <div className="plan-grid">
          {PLANS.map((plan) => {
            const isCurrentPlan = currentPlan === plan.key;
            const isSelected = selected === plan.key;
            return (
              <div
                key={plan.key}
                className={`plan-card ${isSelected ? 'plan-card-selected' : ''} ${isCurrentPlan ? 'plan-card-current' : ''}`}
                onClick={() => !isCurrentPlan && setSelected(plan.key)}
              >
                {plan.popular && !isCurrentPlan && (
                  <div className="plan-badge-popular">Beliebt</div>
                )}
                {isCurrentPlan && (
                  <div className="plan-badge-current">Aktuell</div>
                )}
                <h3 style={{ margin: '0 0 4px', fontSize: '1.1rem' }}>{plan.name}</h3>
                <p style={{ margin: '0 0 4px', color: 'var(--primary)', fontSize: '1.2rem', fontWeight: 700 }}>
                  {plan.price ? `${plan.price} EUR` : 'Auf Anfrage'}
                </p>
                <p className="text-muted" style={{ margin: '0 0 12px', fontSize: '0.75rem' }}>
                  {plan.price ? '/Monat' : ''} — {plan.subtitle}
                </p>
                <ul className="plan-features" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  {plan.features.map((f) => (
                    <li key={f}>
                      <Check size={12} /> {f}
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>

        {isTrial && isUpgrade && (
          <div style={{
            background: 'var(--warning, #f59e0b)11',
            border: '1px solid var(--warning, #f59e0b)33',
            borderRadius: 8,
            padding: '12px 16px',
            marginBottom: 16,
            display: 'flex',
            alignItems: 'center',
            gap: 10,
          }}>
            <Mail size={18} style={{ color: 'var(--warning, #f59e0b)', flexShrink: 0 }} />
            <p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--text)' }}>
              Für ein Upgrade ist eine Zahlungsmethode erforderlich. Kontaktieren Sie uns unter{' '}
              <a href="mailto:service@coachdesk365.com" style={{ color: 'var(--primary)', fontWeight: 600 }}>
                service@coachdesk365.com
              </a>{' '}
              — wir richten Ihr Abo ein.
            </p>
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button className="btn btn-secondary" onClick={onClose}>Abbrechen</button>
          <button
            className="btn btn-primary"
            onClick={handlePlanChange}
            disabled={changing || selected === currentPlan}
          >
            {isTrial && isUpgrade ? <Mail size={16} /> : <Crown size={16} />}
            {getButtonText()}
          </button>
        </div>
      </div>
    </div>
  );
}
